import * as React from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { InputContainer } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import { FormField } from 'common/components/cookbook/FormField';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedWarehouse } from 'common/hooks/useSelectedWarehouse';
import { useWarehouseQuery } from 'common/hooks/useWarehouseQuery';

import { FormValues } from './types';
import { useVendorsQuery } from '../../query';

export const ReferenceIdField = () => {
  const formatMessage = useFormatMessage();

  const selectedWarehouseId = useSelectedWarehouse().id as string;

  const { data: selectedWarehouse } = useWarehouseQuery(selectedWarehouseId);

  const { resetField, watch } = useFormContext<FormValues>();

  const {
    field: { value: referenceTypeValue },
  } = useController<FormValues>({ name: 'referenceType' });

  const watchReferenceType = watch('referenceType');

  React.useEffect(() => {
    resetField('referenceId');
  }, [watchReferenceType, resetField]);

  const isVendorSelectionShown = referenceTypeValue === 'VENDOR';

  const { data: vendorOptions } = useVendorsQuery(
    {
      countryCodes: selectedWarehouse ? [selectedWarehouse.countryCode] : undefined,
    },
    Boolean(selectedWarehouse) && isVendorSelectionShown,
  );

  return (
    <InputContainer
      label={
        isVendorSelectionShown
          ? formatMessage('common.vendor')
          : formatMessage('receiving.reference_number')
      }
      style={{ marginTop: 24 }}
    >
      <FormField
        name="referenceId"
        rules={{ required: formatMessage('common.validation.required') }}
        renderInput={({ ref, ...fieldProps }) =>
          isVendorSelectionShown ? (
            <Select
              {...fieldProps}
              options={vendorOptions}
              placeholder={formatMessage('receiving.vendor_placeholder')}
              selectRef={ref}
            />
          ) : (
            <Input
              placeholder={formatMessage('receiving.reference_number_placeholder')}
              data-test-id="reference-id-input"
              ref={ref}
              {...fieldProps}
            />
          )
        }
      />
    </InputContainer>
  );
};
